// @flow
import { EventEmitter } from 'events';
import StoreDataChangeEvent from './StoreDataChangeEvent';
import getInitialState from './getInitialState';
import _ from 'lodash';
import Storage from '../protocol/Storage';
const debug = require('../debug/debug')('wc:store:Store');

export default class Store extends EventEmitter {
  static GLOBAL_STORE: Store;
  static ALL_STORES: Map<string, Store> = new Map();
  storeName: string;
  map: Map<string, any>;
  /**
   * @param {string} name
   */
  constructor(storeName: string, initialState: Map<any, any> = new Map()) {
    super();
    this.storeName = storeName;
    this.map = initialState;
    this.setMaxListeners(250);
    Store.ALL_STORES.set(this.storeName, this);
  }

  /**
   * Place the data for given in name in store and dispatches the event.
   * @param {String} name
   * @param {*} data
   */
  put<DATA_TYPE>(name: string, data: ?DATA_TYPE) {
    debug('[put()] ', name, data);

    if (Storage.get('StoreTrace')) {
      debug(console.trace());
    }

    const type = this.map.get(name) ? StoreDataChangeEvent.TYPE_EDIT : StoreDataChangeEvent.TYPE_ADD;
    this.map.set(name, data);
    this.emit(name, new StoreDataChangeEvent(this.storeName, name, data, type));
  }

  /**
   *
   * @param {String} name
   * @param {*} defaultValue Returns the default value if map doesn't contains the value
   */
  get<DATA_TYPE>(name: string, defaultValue: ?DATA_TYPE = undefined): ?DATA_TYPE {
    const val: ?DATA_TYPE = this.map.get(name);
    if (val) {
      return val;
    }
    return defaultValue;
  }

  /**
   *
   * @param {String} name
   * @param {boolean} removeAllListeners
   */
  remove(name: string, removeAllListeners: boolean = false) {
    this.map.delete(name);
    this.emit(name, new StoreDataChangeEvent(this.storeName, name, undefined, StoreDataChangeEvent.TYPE_DELETE));
    if (removeAllListeners) {
      this.removeAllListeners(name);
    }
  }

  /**
   * Clear all the event and its listeners.
   * It could be used, then store is not longer required.
   * Note: Method clears the stored data.
   */
  destroy() {
    /**
     * @type {Array.<String>}
     */
    const eventNamesArray = this.eventNames();
    if (_.size(this.eventNamesArray) > 0) {
      eventNamesArray.forEach((eventName) => {
        this.removeAllListeners(eventName);
      });
    }

    this.map.clear();
  }

  /**
   *
   * @param {String} name
   * @param {*} defaultValue
   * @returns {Object}
   */
  static get<DATA_TYPE>(name: string, defaultValue: ?DATA_TYPE = undefined): ?DATA_TYPE {
    return Store.GLOBAL_STORE.get(name, defaultValue);
  }

  /**
   * Add the data in glboal store for given name.
   * @param {String} name Name for the data. Future events will be dispatched on this name.
   * @param {Object} data Data corresponding to given name.
   */
  static put(name: string, data: any) {
    Store.GLOBAL_STORE.put(name, data);
  }

  /**
   * Removes data from store.
   * If "removeAllListeners" is true, then removes all listeners on given name.
   * @param {String} name
   * @param {boolean} removeAllListeners If true, method removes all the event listeners associated with given name.
   */
  static remove(name: string, removeAllListeners: boolean = false) {
    Store.GLOBAL_STORE.remove(name, removeAllListeners);
  }
  /**
   *
   * @param {String} name
   * @param {Function.<StoreDataChangeEvent>} callback
   */
  static addListener(name: string, callback: Function) {
    Store.GLOBAL_STORE.addListener(name, callback);
  }

  /**
   *
   * @param {String} name
   * @param {Function.<StoreDataChangeEvent>} callback
   */
  static once(name: string, callback: Function) {
    Store.GLOBAL_STORE.once(name, callback);
  }

  /**
   *
   * @param {String} name
   * @param {Function.<StoreDataChangeEvent>} callback
   */
  static removeListener(name: string, callback: Function) {
    Store.GLOBAL_STORE.removeListener(name, callback);
  }

  /**
   *
   * @param {String} name
   */
  static removeAllListeners(name: string) {
    Store.GLOBAL_STORE.removeAllListeners(name);
  }

  static printStats() {
    if (Store.GLOBAL_STORE.eventNames) {
      const eventNames = Store.GLOBAL_STORE.eventNames();
      console.log('Event Names in Glboal Store: ', eventNames);
      if (eventNames) {
        eventNames.forEach((name) => {
          console.log('Name: ', name, 'Listener Count: ', Store.GLOBAL_STORE.listenerCount(name));
        });
      }
    } else if (Store.GLOBAL_STORE._events) {
      console.log(Store.GLOBAL_STORE._events);
    }
  }

  static isSubscribed<DATA_TYPE>(
    storeName: string,
    eventName: string,
    callback: (StoreDataChangeEvent<DATA_TYPE>) => void
  ): boolean {
    const store: ?Store = Store.ALL_STORES.get(storeName);
    return store ? store.isSubscribed(eventName, callback) : false;
  }

  isSubscribed<DATA_TYPE>(eventName: string, callback: (StoreDataChangeEvent<DATA_TYPE>) => void): boolean {
    return _.indexOf(this.listeners(eventName), callback) !== -1;
  }

  static subscribe<DATA_TYPE>(
    storeName: string,
    callback: (StoreDataChangeEvent<DATA_TYPE>) => void,
    eventName: string
  ): void {
    const store: ?Store = Store.ALL_STORES.get(storeName);
    if (store) {
      store.subscribe(callback, eventName);
    }
  }

  subscribe<DATA_TYPE>(callback: (StoreDataChangeEvent<DATA_TYPE>) => void, eventName: string): void {
    this.addListener(eventName, callback);
    let data: ?DATA_TYPE = this.get(eventName, null);
    if (data !== null) {
      //If data is already there, fire it on subsribe.
      const type = StoreDataChangeEvent.TYPE_EDIT;
      this.emit(eventName, new StoreDataChangeEvent(this.storeName, eventName, data, type));
    }
  }

  static unsubscribe<DATA_TYPE>(
    storeName: string,
    callback: (StoreDataChangeEvent<DATA_TYPE>) => void,
    eventName: string
  ): void {
    const store: ?Store = Store.ALL_STORES.get(storeName);
    if (store) {
      store.unsubscribe(callback, eventName);
    }
  }

  unsubscribe<DATA_TYPE>(callback: (StoreDataChangeEvent<DATA_TYPE>) => void, eventName: string): void {
    this.removeListener(eventName, callback);
  }

  static GLOBAL_STORE: Store;
}

Store.GLOBAL_STORE = new Store('GLOBAL', getInitialState());

if (process.env.NODE_ENV === 'development') {
  window._store = Store.GLOBAL_STORE;
}
